import { useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import useApiService from '../../services/api'
import { Modal } from '../UI/Modal/Modal'
import { ContactResponse } from 'models/contacts'
import CreatableSelect from 'components/FormUtils/CreatableSelect'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { TextInput } from 'components/FormUtilsV2/TextInput/text-input'
import { StyledEditorRHF } from 'components/Editor/EditorRHF'
import { CgAttachment } from 'react-icons/cg'
import { toast } from 'react-toastify'
import to from 'await-to-js'
import {
  AttachmentList,
  Attachment,
  isNewAttachment,
} from 'components/Attachments/AttachmentList'
import {
  ALLOWED_EMAIL_ATTACHMENT_TYPES,
  MAX_EMAIL_ATTACHMENT_SIZE,
  EMAIL_ATTACHMENT_WARNING_THRESHOLD,
  getAcceptString,
} from '../../constants/fileTypes'
import { useFileAttachments } from '../../hooks/useFileAttachments'
import { handleEmailError } from '../NewCompanyPage/Modals/EmailsModal/handleEmailError'

const formShape = z.object({
  to: z.string({ message: 'Please enter a valid email address' }).email(),
  subject: z.string().min(1),
  from_id: z.number({ message: 'Please select a from email' }),
  body: z.string().min(1),
})

type formValues = z.infer<typeof formShape>

export default function SendEmailModal(props: {
  show: boolean
  handleClose: () => void
  onSubmitSuccess?: () => void
  contact: ContactResponse
}) {
  const api = useApiService()
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const [attachments, setAttachments] = useState<Attachment[]>([])

  const {
    fileInputRef,
    openFileSelect,
    handleFileSelect,
    createRemoveHandler,
  } = useFileAttachments({
    allowedTypes: ALLOWED_EMAIL_ATTACHMENT_TYPES,
    maxFileSize: MAX_EMAIL_ATTACHMENT_SIZE,
    maxTotalSize: MAX_EMAIL_ATTACHMENT_SIZE,
    onAdd: (newAttachments) =>
      setAttachments((prev) => [...prev, ...newAttachments]),
  })

  const { data: emailIntegrationOptions } =
    api.emailIntegration.useGetEmailIntegrationOptions()

  const { control, handleSubmit, setValue } = useForm({
    defaultValues: {
      to: props.contact.email_preferred,
      subject: '',
      body: '',
    } as formValues,
    resolver: zodResolver(formShape),
    mode: 'all',
  })

  useMemo(() => {
    if (emailIntegrationOptions?.length === 1) {
      setValue('from_id', emailIntegrationOptions[0].value)
    }
  }, [emailIntegrationOptions])

  const toOptions = useMemo(() => {
    const options = []
    if (props.contact.email_preferred) {
      options.push({
        label: props.contact.email_preferred,
        value: props.contact.email_preferred,
      })
    }
    if (props.contact.email_work) {
      options.push({
        label: props.contact.email_work,
        value: props.contact.email_work,
      })
    }
    if (props.contact.email_personal) {
      options.push({
        label: props.contact.email_personal,
        value: props.contact.email_personal,
      })
    }
    if (props.contact.email_generic) {
      options.push({
        label: props.contact.email_generic,
        value: props.contact.email_generic,
      })
    }
    // Check unique values
    const uniqueOptions = options.filter(
      (option, index, self) =>
        index === self.findIndex((o) => o.value === option.value)
    )
    return uniqueOptions
  }, [props.contact])

  const handleRemoveAttachment = createRemoveHandler((index: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index))
  })

  const onSubmit = handleSubmit(async (values) => {
    setIsSubmitting(true)

    try {
      // Build FormData payload
      const formData = new FormData()

      // Add email metadata
      formData.append(
        'to',
        JSON.stringify([
          {
            name: `${props.contact.first_name} ${props.contact.last_name}`,
            email: values.to,
          },
        ])
      )
      formData.append('subject', values.subject)
      formData.append('body', values.body)

      // Add file attachments directly (only new attachments, not existing ones)
      attachments.forEach((attachment) => {
        if (isNewAttachment(attachment)) {
          formData.append('attachments', attachment.file)
        }
      })

      const [err] = await to(
        api.emailIntegration.sendEmailMessage(values.from_id, formData)
      )

      setIsSubmitting(false)

      if (err) {
        await handleEmailError(err, api.emailIntegration)
        return
      }

      toast.success('Email sent successfully')
      props.onSubmitSuccess?.()
      props.handleClose()
    } catch (error) {
      setIsSubmitting(false)
      console.error('Error sending email:', error)
      toast.error('Failed to send email. Please try again.')
    }
  })

  return (
    <Modal
      size="lg"
      open={props.show}
      onOpenChange={(open) => !open && props.handleClose()}
      title={'Send Email'}
      onAccept={onSubmit}
      acceptButtonText={isSubmitting ? 'Sending...' : 'Send'}
      loading={isSubmitting}
      cancelButtonText={isSubmitting ? 'Cancel' : 'Close'}
      onCancel={props.handleClose}
    >
      <CreatableSelect
        label={'To'}
        control={control}
        name="to"
        placeholder="To email"
        disableCreate
        options={toOptions}
        className={'flex-1 mt-0'}
        onCreateOption={() => Promise.resolve(null)}
        isClearable={false}
      />
      <CreatableSelect
        label={'From'}
        control={control}
        name="from_id"
        placeholder="From email"
        disableCreate
        options={emailIntegrationOptions}
        className={'flex-1 mt-0'}
        onCreateOption={() => Promise.resolve(null)}
        isClearable={false}
      />
      <TextInput
        control={control}
        name="subject"
        type="text"
        label="Subject"
        placeholder="Subject"
      />
      <>
        <StyledEditorRHF
          name={'body'}
          className={'min-h-28'}
          control={control}
          label={'Email Content'}
          placeholder={'Email Content'}
          additionalToolbarButtons={[
            <CgAttachment
              key="attachments"
              size={24}
              className={`p-1 w-8 ${
                isSubmitting
                  ? 'cursor-not-allowed opacity-50'
                  : 'cursor-pointer hover:text-brand-active'
              }`}
              onClick={isSubmitting ? undefined : openFileSelect}
            />,
          ]}
        />

        <AttachmentList
          attachments={attachments}
          onRemove={handleRemoveAttachment}
          disabled={isSubmitting}
          maxSize={MAX_EMAIL_ATTACHMENT_SIZE}
          warningThreshold={EMAIL_ATTACHMENT_WARNING_THRESHOLD}
        />

        <input
          type="file"
          hidden
          multiple
          ref={fileInputRef}
          onChange={(e) => handleFileSelect(e, attachments)}
          accept={getAcceptString(ALLOWED_EMAIL_ATTACHMENT_TYPES)}
          disabled={isSubmitting}
        />
      </>
    </Modal>
  )
}
